import { useRef } from 'react'

// Verdicts based on safety factor
function getVerdict(sf) {
  if (sf >= 5) return { label: 'Very Safe', color: 'text-green-400', bg: 'bg-green-900/30 border-green-700', icon: 'shield-check', emoji: 'shield', plain: 'Your part is well over-engineered for this load. It can handle much more than what you\'re asking of it.' }
  if (sf >= 3) return { label: 'Safe', color: 'text-green-400', bg: 'bg-green-900/30 border-green-700', icon: 'shield-check', emoji: 'shield', plain: 'Excellent. Your part can comfortably withstand this load with a generous margin.' }
  if (sf >= 1.5) return { label: 'Adequate', color: 'text-blue-400', bg: 'bg-blue-900/30 border-blue-700', icon: 'check', emoji: 'ok', plain: 'Your part will hold, but the margin is moderate. Fine for most non-critical uses.' }
  if (sf >= 1) return { label: 'Marginal', color: 'text-yellow-400', bg: 'bg-yellow-900/30 border-yellow-700', icon: 'alert', emoji: 'warning', plain: 'Your part barely survives this load. Any unexpected force, vibration, or defect could cause failure. Consider a stronger material or thicker design.' }
  return { label: 'Will Fail', color: 'text-red-400', bg: 'bg-red-900/30 border-red-700', icon: 'x', emoji: 'danger', plain: 'The stress exceeds the material\'s strength. This part WILL permanently deform or break under this load. You must redesign or choose a stronger material.' }
}

function stressAnalogy(mpa) {
  if (mpa < 1) return 'Less than the pressure from resting your finger on a table.'
  if (mpa < 10) return 'Similar to the pressure inside a car tire.'
  if (mpa < 50) return 'Like the force on a bicycle spoke during a ride.'
  if (mpa < 200) return 'Comparable to stress in a structural steel beam supporting a floor.'
  if (mpa < 500) return 'Similar to stress experienced by aircraft landing gear on touchdown.'
  if (mpa < 1000) return 'Approaching the limits of high-strength steel and titanium alloys.'
  return 'Extreme stress — beyond what most engineering materials can withstand.'
}

function displacementAnalogy(mm) {
  if (mm < 0.001) return 'Virtually no movement — less than the width of a bacteria.'
  if (mm < 0.01) return 'Barely measurable — thinner than a human hair.'
  if (mm < 0.1) return 'About the thickness of a sheet of paper.'
  if (mm < 1) return 'Visible under a magnifying glass but not to the naked eye.'
  if (mm < 5) return 'Noticeable flex — like pressing on a plastic ruler.'
  return 'Significant deformation — the part is visibly bending.'
}

function forceAnalogy(n) {
  if (n < 10) return `About ${(n / 9.81).toFixed(1)} kg — like holding a ${n < 5 ? 'smartphone' : 'small book'}.`
  if (n < 100) return `About ${(n / 9.81).toFixed(1)} kg — like a ${n < 50 ? 'bag of rice' : 'loaded backpack'}.`
  if (n < 1000) return `About ${(n / 9.81).toFixed(0)} kg — like a ${n < 500 ? 'large suitcase' : 'person standing on it'}.`
  if (n < 10000) return `About ${(n / 9.81).toFixed(0)} kg — comparable to ${n < 5000 ? 'a grand piano' : 'a small car'} pressing down.`
  return `About ${(n / 9.81).toFixed(0)} kg — equivalent to the weight of ${(n / 9810).toFixed(1)} metric tons.`
}

export default function ReportPanel({ summary, materials, selectedMaterial, stlFile, onClose }) {
  const reportRef = useRef()

  if (!summary) return null

  const r = summary.results
  const m = summary.mesh
  const mat = summary.material
  const bc = summary.boundary_conditions
  const sf = r.safety_factor
  const verdict = getVerdict(sf)
  const matInfo = materials?.[selectedMaterial]

  const handlePrint = () => {
    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    const content = reportRef.current.innerHTML
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
<title>Osteo3d FEA${stlFile ? ' — ' + stlFile.name : ''}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1a1a1a; padding: 40px; max-width: 800px; margin: 0 auto; line-height: 1.5; }
  .report-header { text-align: center; border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; margin-bottom: 30px; }
  .report-header h1 { font-size: 22px; margin-bottom: 4px; }
  .report-header p { color: #6b7280; font-size: 12px; }
  .verdict-box { text-align: center; padding: 24px; border: 2px solid; border-radius: 12px; margin-bottom: 24px; }
  .verdict-box.safe { border-color: #22c55e; background: #f0fdf4; }
  .verdict-box.adequate { border-color: #3b82f6; background: #eff6ff; }
  .verdict-box.marginal { border-color: #eab308; background: #fefce8; }
  .verdict-box.fail { border-color: #ef4444; background: #fef2f2; }
  .verdict-label { font-size: 28px; font-weight: 800; }
  .verdict-box.safe .verdict-label { color: #16a34a; }
  .verdict-box.adequate .verdict-label { color: #2563eb; }
  .verdict-box.marginal .verdict-label { color: #ca8a04; }
  .verdict-box.fail .verdict-label { color: #dc2626; }
  .verdict-plain { font-size: 13px; color: #4b5563; margin-top: 8px; }
  .sf-number { font-size: 48px; font-weight: 900; margin: 8px 0; }
  .section { margin-bottom: 24px; }
  .section h2 { font-size: 15px; font-weight: 700; color: #374151; margin-bottom: 12px; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
  .metric-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 10px 0; border-bottom: 1px solid #f3f4f6; }
  .metric-label { font-size: 12px; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; min-width: 140px; }
  .metric-value { font-size: 14px; font-weight: 600; color: #111; text-align: right; }
  .metric-plain { font-size: 11px; color: #6b7280; margin-top: 2px; font-style: italic; text-align: right; max-width: 380px; }
  .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .info-card { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
  .info-card .label { font-size: 10px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; }
  .info-card .value { font-size: 16px; font-weight: 700; color: #111; margin-top: 2px; }
  .info-card .sub { font-size: 11px; color: #6b7280; margin-top: 2px; }
  .layman-box { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px; padding: 12px 14px; margin-top: 8px; }
  .layman-box p { font-size: 12px; color: #0c4a6e; }
  .layman-box .label { font-size: 10px; font-weight: 700; color: #0284c7; margin-bottom: 4px; text-transform: uppercase; }
  .footer { text-align: center; color: #9ca3af; font-size: 10px; margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; }
  svg { max-width: 16px; max-height: 16px; }
  .pdf-header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 16px; border-bottom: 2px solid #111; margin-bottom: 24px; }
  .pdf-header img { height: 36px; }
  .pdf-header .right { text-align: right; font-size: 10px; color: #9ca3af; }
  .pdf-header .right .title { font-size: 13px; font-weight: 700; color: #374151; }
  .pdf-footer { text-align: center; font-size: 9px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 12px; margin-top: 32px; }
  .pdf-footer .legal { margin-top: 4px; font-size: 8px; }
  @media print {
    body { padding: 20px; }
    svg { max-width: 16px; max-height: 16px; }
    .pdf-header { position: running(header); }
    .pdf-footer { position: running(footer); }
  }
</style>
</head>
<body>
<div class="pdf-header">
  <img src="/osteo3d-logo.png" alt="Osteo3d" />
  <div class="right">
    <div class="title">Structural Analysis Report</div>
    <div>${stlFile?.name || 'FEA Analysis'} &middot; ${dateStr}</div>
  </div>
</div>
${content}
<div class="pdf-footer">
  <div>&copy; ${new Date().getFullYear()} Osteo3d &middot; 3D Printing Healthcare &middot; www.osteo3d.com</div>
  <div class="legal">This report is generated by Osteo3d FEA for informational purposes only. Results should be validated by a qualified engineer before use in production or safety-critical applications. All rights reserved.</div>
  <div style="margin-top: 4px;">Generated on ${dateStr}</div>
</div>
</body>
</html>`)
    printWindow.document.close()
    setTimeout(() => printWindow.print(), 300)
  }

  const verdictClass = sf >= 3 ? 'safe' : sf >= 1.5 ? 'adequate' : sf >= 1 ? 'marginal' : 'fail'
  const totalForce = bc?.total_force_n || Math.sqrt(r.load_vector?.reduce((s, v) => s + v * v, 0) || 0)
  const dateStr = new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })

  return (
    <div className="fixed inset-0 z-50 bg-gray-950/95 backdrop-blur-sm overflow-y-auto">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 bg-gray-900 border-b border-gray-800 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5 text-sm">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Viewer
          </button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">Osteo3d FEA</span>
          <button onClick={handlePrint}
            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export PDF
          </button>
        </div>
      </div>

      {/* Report content — also used for print */}
      <div className="max-w-3xl mx-auto p-6" ref={reportRef}>
        {/* Verdict */}
        <div className={`verdict-box ${verdictClass} text-center py-6 rounded-xl border-2 mb-8 ${verdict.bg}`}>
          <p className="text-xs text-gray-400 uppercase tracking-widest mb-1 print:text-gray-600">Overall Verdict</p>
          <p className={`verdict-label text-3xl font-black ${verdict.color}`}>{verdict.label}</p>
          <p className="sf-number text-5xl font-black text-white mt-2 print:text-black">
            {sf >= 9999 ? 'N/A' : sf.toFixed(2)}
          </p>
          <p className="text-xs text-gray-400 print:text-gray-600">Safety Factor</p>
          <p className="verdict-plain text-sm text-gray-400 mt-3 max-w-md mx-auto print:text-gray-700">
            {verdict.plain}
          </p>
        </div>

        {/* What does Safety Factor mean? */}
        <div className="layman-box bg-blue-950/30 border border-blue-800/50 rounded-xl p-4 mb-8">
          <p className="label text-[10px] text-blue-400 font-bold uppercase tracking-wider mb-2">What is Safety Factor?</p>
          <p className="text-sm text-blue-200/80 leading-relaxed">
            Safety Factor tells you how far your part is from breaking.
            A value of <strong>1.0</strong> means the part is at its exact limit — any more force and it fails.
            A value of <strong>3.0</strong> means the part can handle <strong>3 times</strong> the applied load before it starts to permanently deform.
            Most engineering standards require at least <strong>1.5 to 3.0</strong> depending on the application.
          </p>
        </div>

        {/* Key Results */}
        <div className="section mb-8">
          <h2 className="text-sm font-bold text-gray-300 uppercase tracking-wider mb-4 border-b border-gray-800 pb-2 print:text-black print:border-gray-300">
            Key Results
          </h2>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <ResultCard
              label="Maximum Stress"
              value={`${r.max_von_mises_mpa} MPa`}
              sub={`Yield limit: ${(mat.yield_strength / 1e6).toFixed(0)} MPa`}
              status={r.yield_exceeded ? 'danger' : sf < 1.5 ? 'warning' : 'ok'}
            />
            <ResultCard
              label="Maximum Displacement"
              value={`${r.max_displacement_mm} mm`}
              sub=""
              status="neutral"
            />
            <ResultCard
              label="Safety Factor"
              value={sf >= 9999 ? 'N/A' : sf.toFixed(2)}
              sub={verdict.label}
              status={sf >= 3 ? 'ok' : sf >= 1 ? 'warning' : 'danger'}
            />
            <ResultCard
              label="Total Applied Force"
              value={`${totalForce.toFixed(0)} N`}
              sub={`on ${bc?.load_nodes_count || '—'} nodes`}
              status="neutral"
            />
          </div>

          {/* Layman interpretations */}
          <div className="space-y-3">
            <InterpretRow
              label="Maximum Stress"
              technical={`The highest Von Mises stress in the model is ${r.max_von_mises_mpa} MPa. ${r.yield_exceeded ? `This EXCEEDS the material's yield strength of ${(mat.yield_strength / 1e6).toFixed(0)} MPa.` : `This is within the material's yield strength of ${(mat.yield_strength / 1e6).toFixed(0)} MPa.`}`}
              plain={stressAnalogy(r.max_von_mises_mpa)}
            />
            <InterpretRow
              label="Displacement"
              technical={`The maximum nodal displacement is ${r.max_displacement_mm} mm under the applied load.`}
              plain={displacementAnalogy(r.max_displacement_mm)}
            />
            <InterpretRow
              label="Applied Force"
              technical={`A total force of ${totalForce.toFixed(1)} N was distributed across ${bc?.load_nodes_count || '—'} nodes in the load region.`}
              plain={forceAnalogy(totalForce)}
            />
          </div>
        </div>

        {/* Material */}
        <div className="section mb-8">
          <h2 className="text-sm font-bold text-gray-300 uppercase tracking-wider mb-4 border-b border-gray-800 pb-2 print:text-black print:border-gray-300">
            Material Properties
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <PropRow label="Material" value={mat.name} />
            <PropRow label="Young's Modulus" value={`${(mat.young_modulus / 1e9).toFixed(1)} GPa`} hint="How stiff the material is — higher = less flex" />
            <PropRow label="Yield Strength" value={`${(mat.yield_strength / 1e6).toFixed(0)} MPa`} hint="Maximum stress before permanent deformation" />
            <PropRow label="Poisson's Ratio" value={mat.poisson_ratio} hint="How much the material squeezes sideways when compressed" />
            <PropRow label="Density" value={`${mat.density} kg/m³`} hint="Weight per unit volume" />
          </div>
          <div className="layman-box bg-blue-950/30 border border-blue-800/50 rounded-xl p-4 mt-4">
            <p className="label text-[10px] text-blue-400 font-bold uppercase tracking-wider mb-2">In Simple Terms</p>
            <p className="text-sm text-blue-200/80 leading-relaxed">
              {mat.young_modulus > 100e9
                ? `${mat.name} is a very stiff material — it resists bending strongly. `
                : mat.young_modulus > 10e9
                ? `${mat.name} has moderate stiffness — it will flex slightly under load. `
                : `${mat.name} is a flexible material — expect noticeable bending under load. `}
              {mat.yield_strength > 500e6
                ? 'It has high strength, suitable for load-bearing applications.'
                : mat.yield_strength > 100e6
                ? 'Its strength is moderate — fine for most general-purpose uses.'
                : 'It has relatively low strength — best for light-duty applications.'}
            </p>
          </div>
        </div>

        {/* Mesh Info */}
        <div className="section mb-8">
          <h2 className="text-sm font-bold text-gray-300 uppercase tracking-wider mb-4 border-b border-gray-800 pb-2 print:text-black print:border-gray-300">
            Mesh & Model
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <PropRow label="Elements" value={m.num_elements?.toLocaleString()} hint="Number of tiny pieces the model is divided into" />
            <PropRow label="Nodes" value={m.num_nodes?.toLocaleString()} hint="Number of calculation points" />
            <PropRow label="Mesh Size" value={`${m.mesh_size?.toFixed(2) || '—'} mm`} hint="Average element size" />
            <PropRow label="Model" value={stlFile?.name || '—'} />
          </div>
          <div className="layman-box bg-blue-950/30 border border-blue-800/50 rounded-xl p-4 mt-4">
            <p className="label text-[10px] text-blue-400 font-bold uppercase tracking-wider mb-2">What is meshing?</p>
            <p className="text-sm text-blue-200/80 leading-relaxed">
              Your 3D model was automatically divided into {m.num_elements?.toLocaleString()} tiny tetrahedral (pyramid-shaped) pieces.
              The computer then calculates stress and displacement at each of the {m.num_nodes?.toLocaleString()} corner points.
              More elements = more accurate results but slower computation.
            </p>
          </div>
        </div>

        {/* Boundary Conditions */}
        <div className="section mb-8">
          <h2 className="text-sm font-bold text-gray-300 uppercase tracking-wider mb-4 border-b border-gray-800 pb-2 print:text-black print:border-gray-300">
            Boundary Conditions
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <PropRow label="Fixed Nodes" value={bc?.fixed_nodes_count || '—'} hint="Points where the part is held in place" />
            <PropRow label="Load Nodes" value={bc?.load_nodes_count || '—'} hint="Points where force is applied" />
            <PropRow label="Load Direction" value={formatLoadVector(r.load_vector || bc?.load_vector)} />
            <PropRow label="Total Force" value={`${totalForce.toFixed(1)} N`} />
          </div>
          <div className="layman-box bg-blue-950/30 border border-blue-800/50 rounded-xl p-4 mt-4">
            <p className="label text-[10px] text-blue-400 font-bold uppercase tracking-wider mb-2">In Simple Terms</p>
            <p className="text-sm text-blue-200/80 leading-relaxed">
              One end of your part was held firmly in place (like clamping it in a vise),
              while a force of {totalForce.toFixed(0)} N ({(totalForce / 9.81).toFixed(1)} kg) was applied to the other end.
              This simulates what would happen in real use.
            </p>
          </div>
        </div>

        {/* Load Capacity Analysis — computed from linear scaling */}
        <LoadSweepSection
          appliedForce={totalForce}
          maxStressMpa={r.max_von_mises_mpa}
          maxDispMm={r.max_displacement_mm}
          yieldStrengthMpa={mat.yield_strength / 1e6}
          safetyFactor={sf}
        />

        {/* Recommendations */}
        <div className="section mb-8">
          <h2 className="text-sm font-bold text-gray-300 uppercase tracking-wider mb-4 border-b border-gray-800 pb-2 print:text-black print:border-gray-300">
            Recommendations
          </h2>
          <div className="space-y-2">
            {sf < 1 && (
              <>
                <Rec type="danger" text="This design will fail under the specified load. Redesign is required." />
                <Rec type="suggestion" text="Increase wall thickness or add ribbing in high-stress areas." />
                <Rec type="suggestion" text="Consider a stronger material with higher yield strength." />
                <Rec type="suggestion" text="Reduce the applied load or distribute it over a larger area." />
              </>
            )}
            {sf >= 1 && sf < 1.5 && (
              <>
                <Rec type="warning" text="The safety margin is very thin. Not recommended for critical or fatigue-loaded applications." />
                <Rec type="suggestion" text="Add material in areas of peak stress to increase the safety factor." />
                <Rec type="suggestion" text="Run a fatigue analysis if the load is cyclic (repeated loading)." />
              </>
            )}
            {sf >= 1.5 && sf < 3 && (
              <>
                <Rec type="ok" text="The part meets basic structural requirements." />
                <Rec type="suggestion" text="For medical implants or safety-critical parts, a safety factor of 3+ is typically required." />
              </>
            )}
            {sf >= 3 && (
              <>
                <Rec type="ok" text="The part has an excellent safety margin." />
                <Rec type="suggestion" text="If weight or cost is a concern, you may be able to reduce material while maintaining adequate safety." />
              </>
            )}
            <Rec type="note" text="This is a linear static analysis. It does not account for fatigue, impact, thermal effects, or manufacturing defects." />
          </div>
        </div>

      </div>
    </div>
  )
}

function ResultCard({ label, value, sub, status }) {
  const colors = {
    ok: 'border-green-800/50 bg-green-950/20',
    warning: 'border-yellow-800/50 bg-yellow-950/20',
    danger: 'border-red-800/50 bg-red-950/20',
    neutral: 'border-gray-800 bg-gray-900/50',
  }
  const textColors = {
    ok: 'text-green-400',
    warning: 'text-yellow-400',
    danger: 'text-red-400',
    neutral: 'text-white',
  }

  return (
    <div className={`info-card rounded-xl border p-4 ${colors[status]}`}>
      <p className="label text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      <p className={`value text-2xl font-bold mt-1 ${textColors[status]} print:text-black`}>{value}</p>
      {sub && <p className="sub text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  )
}

function InterpretRow({ label, technical, plain }) {
  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 print:bg-white print:border-gray-200">
      <p className="text-xs font-bold text-gray-300 uppercase tracking-wider mb-2 print:text-black">{label}</p>
      <p className="metric-value text-sm text-gray-300 leading-relaxed print:text-gray-800">{technical}</p>
      <div className="layman-box bg-blue-950/30 border border-blue-800/30 rounded-lg p-3 mt-3 print:bg-blue-50 print:border-blue-200">
        <p className="text-[10px] text-blue-400 font-bold uppercase tracking-wider mb-1 print:text-blue-700">In Plain English</p>
        <p className="metric-plain text-sm text-blue-200/80 not-italic text-left max-w-none print:text-blue-900">{plain}</p>
      </div>
    </div>
  )
}

function PropRow({ label, value, hint }) {
  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-3 print:bg-white print:border-gray-200">
      <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="text-sm text-white font-semibold mt-0.5 print:text-black">{value}</p>
      {hint && <p className="text-[10px] text-gray-600 mt-0.5 italic">{hint}</p>}
    </div>
  )
}

function Rec({ type, text }) {
  const styles = {
    danger: 'bg-red-950/30 border-red-800/50 text-red-300',
    warning: 'bg-yellow-950/30 border-yellow-800/50 text-yellow-300',
    ok: 'bg-green-950/30 border-green-800/50 text-green-300',
    suggestion: 'bg-gray-900/50 border-gray-700 text-gray-300',
    note: 'bg-gray-900/30 border-gray-800 text-gray-500 italic',
  }
  const icons = {
    danger: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.999L13.732 4.002c-.77-1.333-2.694-1.333-3.464 0L3.34 16.002c-.77 1.332.192 2.999 1.732 2.999z',
    warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.999L13.732 4.002c-.77-1.333-2.694-1.333-3.464 0L3.34 16.002c-.77 1.332.192 2.999 1.732 2.999z',
    ok: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    suggestion: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
    note: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  }

  return (
    <div className={`flex items-start gap-3 rounded-lg border p-3 ${styles[type]}`}>
      <svg width="16" height="16" style={{ width: 16, height: 16, minWidth: 16, flexShrink: 0, marginTop: 2 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d={icons[type]} />
      </svg>
      <p className="text-sm leading-relaxed">{text}</p>
    </div>
  )
}

function LoadSweepSection({ appliedForce, maxStressMpa, maxDispMm, yieldStrengthMpa, safetyFactor }) {
  if (!appliedForce || !maxStressMpa || maxStressMpa <= 0) return null

  // Linear FEA: stress and displacement scale linearly with load
  const stressPerN = maxStressMpa / appliedForce  // MPa per Newton
  const dispPerN = maxDispMm / appliedForce       // mm per Newton

  // Critical loads
  const yieldLoad = yieldStrengthMpa / stressPerN  // Load at SF=1.0 (yield)
  const safeLoad15 = yieldLoad / 1.5               // Load at SF=1.5
  const safeLoad30 = yieldLoad / 3.0               // Load at SF=3.0

  // Build sweep table: 10%, 25%, 50%, 75%, 100%, 150%, 200%, and critical load
  const multipliers = [0.1, 0.25, 0.5, 0.75, 1.0, 1.5, 2.0, 3.0, 5.0]
  const rows = multipliers.map(mult => {
    const load = appliedForce * mult
    const stress = maxStressMpa * mult
    const disp = maxDispMm * mult
    const sf = yieldStrengthMpa / stress
    return { load, stress, disp, sf, mult, isApplied: mult === 1.0 }
  })

  // Add the exact yield load if it's not already close to an existing row
  const yieldMult = yieldLoad / appliedForce
  const hasCloseRow = multipliers.some(m => Math.abs(m - yieldMult) < 0.05)
  if (!hasCloseRow && yieldMult > 0.05 && yieldMult < 10) {
    rows.push({
      load: yieldLoad,
      stress: yieldStrengthMpa,
      disp: dispPerN * yieldLoad,
      sf: 1.0,
      mult: yieldMult,
      isYield: true,
    })
    rows.sort((a, b) => a.load - b.load)
  }

  // Filter to reasonable range (up to 2x past yield or 5x applied, whichever is less)
  const maxShow = Math.min(yieldLoad * 2, appliedForce * 5)
  const visibleRows = rows.filter(r => r.load <= maxShow + 1)

  // Bar chart max for visual scaling
  const barMax = yieldStrengthMpa * 1.2

  return (
    <div className="section mb-8">
      <h2 className="text-sm font-bold text-gray-300 uppercase tracking-wider mb-4 border-b border-gray-800 pb-2 print:text-black print:border-gray-300">
        Load Capacity Analysis
      </h2>

      {/* Hero: Max safe load */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-green-950/20 border-2 border-green-800/50 rounded-xl p-4 text-center">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Max Safe Load (SF 3.0)</p>
          <p className="text-2xl font-black text-green-400 mt-1">{safeLoad30.toFixed(0)} N</p>
          <p className="text-xs text-gray-500 mt-1">{(safeLoad30 / 9.81).toFixed(1)} kg</p>
          <p className="text-[10px] text-gray-600 mt-1 italic">{forceAnalogy(safeLoad30)}</p>
        </div>
        <div className="bg-red-950/20 border-2 border-red-800/50 rounded-xl p-4 text-center">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Breaking Load (SF 1.0)</p>
          <p className="text-2xl font-black text-red-400 mt-1">{yieldLoad.toFixed(0)} N</p>
          <p className="text-xs text-gray-500 mt-1">{(yieldLoad / 9.81).toFixed(1)} kg</p>
          <p className="text-[10px] text-gray-600 mt-1 italic">{forceAnalogy(yieldLoad)}</p>
        </div>
      </div>

      {/* Layman explanation */}
      <div className="layman-box bg-blue-950/30 border border-blue-800/50 rounded-xl p-4 mb-4">
        <p className="label text-[10px] text-blue-400 font-bold uppercase tracking-wider mb-2">How to read this</p>
        <p className="text-sm text-blue-200/80 leading-relaxed">
          Since stress scales linearly with force, we can predict how your part behaves at <strong>any</strong> load from a single analysis.
          Your part can safely handle up to <strong>{safeLoad30.toFixed(0)} N ({(safeLoad30 / 9.81).toFixed(1)} kg)</strong> with a comfortable safety margin.
          It will start to permanently deform at <strong>{yieldLoad.toFixed(0)} N ({(yieldLoad / 9.81).toFixed(1)} kg)</strong>.
          {appliedForce < safeLoad30
            ? ` Your tested load of ${appliedForce.toFixed(0)} N is well within the safe range.`
            : appliedForce < yieldLoad
            ? ` Your tested load of ${appliedForce.toFixed(0)} N is within limits but leaves little margin.`
            : ` Your tested load of ${appliedForce.toFixed(0)} N exceeds the material's capacity.`}
        </p>
      </div>

      {/* Load sweep table */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-800 text-gray-500">
              <th className="text-left p-3 font-medium">Load</th>
              <th className="text-right p-3 font-medium">Stress</th>
              <th className="text-right p-3 font-medium">Displacement</th>
              <th className="text-right p-3 font-medium">Safety Factor</th>
              <th className="p-3 font-medium w-32">Status</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, i) => {
              const sfColor = row.sf >= 3 ? 'text-green-400' : row.sf >= 1.5 ? 'text-blue-400' : row.sf >= 1 ? 'text-yellow-400' : 'text-red-400'
              const sfLabel = row.sf >= 3 ? 'Safe' : row.sf >= 1.5 ? 'Adequate' : row.sf >= 1 ? 'Marginal' : 'FAIL'
              const barWidth = Math.min(100, (row.stress / barMax) * 100)
              const barColor = row.sf >= 3 ? 'bg-green-500' : row.sf >= 1.5 ? 'bg-blue-500' : row.sf >= 1 ? 'bg-yellow-500' : 'bg-red-500'
              const rowBg = row.isApplied ? 'bg-blue-950/30' : row.isYield ? 'bg-red-950/20' : i % 2 === 0 ? 'bg-transparent' : 'bg-gray-900/30'

              return (
                <tr key={i} className={`border-b border-gray-800/50 ${rowBg}`}>
                  <td className="p-3">
                    <span className="text-gray-200 font-mono font-semibold">{row.load.toFixed(0)} N</span>
                    <span className="text-gray-600 ml-1.5">({(row.load / 9.81).toFixed(1)} kg)</span>
                    {row.isApplied && <span className="text-blue-400 text-[9px] ml-1.5 font-bold">YOUR TEST</span>}
                    {row.isYield && <span className="text-red-400 text-[9px] ml-1.5 font-bold">YIELD POINT</span>}
                  </td>
                  <td className="p-3 text-right font-mono text-gray-300">{row.stress.toFixed(1)} MPa</td>
                  <td className="p-3 text-right font-mono text-gray-300">{row.disp.toFixed(3)} mm</td>
                  <td className="p-3 text-right">
                    <span className={`font-bold font-mono ${sfColor}`}>{row.sf.toFixed(2)}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-800 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full ${barColor} transition-all`} style={{ width: `${barWidth}%` }} />
                      </div>
                      <span className={`text-[9px] font-bold ${sfColor} w-14 text-right`}>{sfLabel}</span>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Visual load capacity bar */}
      <div className="mt-4 bg-gray-900/50 border border-gray-800 rounded-xl p-4">
        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-3">Load Capacity Visualisation</p>
        <div className="relative h-8 bg-gray-800 rounded-full overflow-hidden">
          {/* Safe zone */}
          <div className="absolute inset-y-0 left-0 bg-green-900/50 rounded-l-full"
            style={{ width: `${Math.min(100, (safeLoad30 / (yieldLoad * 1.3)) * 100)}%` }} />
          {/* Adequate zone */}
          <div className="absolute inset-y-0 bg-yellow-900/30"
            style={{ left: `${(safeLoad30 / (yieldLoad * 1.3)) * 100}%`, width: `${((safeLoad15 - safeLoad30) / (yieldLoad * 1.3)) * 100}%` }} />
          {/* Marginal zone */}
          <div className="absolute inset-y-0 bg-orange-900/30"
            style={{ left: `${(safeLoad15 / (yieldLoad * 1.3)) * 100}%`, width: `${((yieldLoad - safeLoad15) / (yieldLoad * 1.3)) * 100}%` }} />
          {/* Fail zone */}
          <div className="absolute inset-y-0 right-0 bg-red-900/40 rounded-r-full"
            style={{ left: `${Math.min(100, (yieldLoad / (yieldLoad * 1.3)) * 100)}%` }} />

          {/* Your load marker */}
          <div className="absolute inset-y-0 w-0.5 bg-white shadow-lg shadow-white/50"
            style={{ left: `${Math.min(98, (appliedForce / (yieldLoad * 1.3)) * 100)}%` }}>
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] text-white font-bold whitespace-nowrap bg-gray-900 px-1.5 py-0.5 rounded">
              You: {appliedForce.toFixed(0)} N
            </div>
          </div>
        </div>
        <div className="flex justify-between mt-2 text-[9px] text-gray-600">
          <span>0 N</span>
          <span className="text-green-600">Safe ({safeLoad30.toFixed(0)} N)</span>
          <span className="text-yellow-600">Adequate ({safeLoad15.toFixed(0)} N)</span>
          <span className="text-red-600">Yield ({yieldLoad.toFixed(0)} N)</span>
        </div>
      </div>
    </div>
  )
}

function formatLoadVector(vec) {
  if (!vec) return '—'
  const labels = ['X', 'Y', 'Z']
  const parts = vec.map((v, i) => v !== 0 ? `${labels[i]}: ${v} N` : null).filter(Boolean)
  return parts.join(', ') || '0 N'
}
